/** Route line appearance — Colour + Weight, persisted per browser
 * (localStorage key "routeLook"), NOT per trip and NOT carried in share
 * links (Lee's Sep 14 2026 ask: "I can barely see the route on the map…
 * make sure that is an option for the user to change"). Mirrors the iOS
 * contract's RouteLook (Support/RouteLook.swift on that platform) as
 * string unions rather than a Swift enum: five saturated colours a rider
 * would actually say out loud, four weights, and a casing chosen PER
 * COLOUR so the line always separates from the map underneath it — see
 * ROUTE_COLORS' own comment for exactly why the split falls where it
 * does.
 *
 * Pure and node-safe: no DOM/map types imported here (only `localStorage`
 * itself, guarded the same way plan/store.ts guards it), so this module is
 * testable without a browser (tests/plan-routelook.test.ts) and safe to
 * import from both RouteOverlay.ts (map paint) and LookMenu.tsx (the
 * picker UI) without dragging maplibre-gl into either. */

export type RouteColor = "cobalt" | "magenta" | "lime" | "cyan" | "amber";
export type RouteWeight = "thin" | "regular" | "bold" | "heavy";

export interface RouteLook {
  color: RouteColor;
  weight: RouteWeight;
}

/** theme.css --soilink — the dark casing option, used under the light/
 * bright colours (Lime, Cyan, Amber) so the line still separates from a
 * pale basemap or this app's own paledust ground; a cream casing under
 * those three all but vanishes there. */
const SOILINK_CASING = "#3b3226";
/** The cream casing RouteOverlay.ts already painted under every route line
 * before this setting existed — kept as the exact same hex so Cobalt +
 * Regular (the default) repaints pixel-for-pixel unchanged. Used under the
 * dark/deep colours (Cobalt, Ember, Magenta), which already contrast
 * against cream on their own. */
const CREAM_CASING = "#fbf6ea";

/** Canonical UI order — the order Lee's spec lists them in, and the order
 * LookMenu renders the swatches. Object.keys(ROUTE_COLORS) would iterate
 * in this same insertion order for plain string keys, but an explicit
 * array keeps that a guarantee rather than an incidental JS engine detail
 * a future edit could quietly break by reordering the record's fields. */
export const ROUTE_COLOR_ORDER: readonly RouteColor[] = ["cobalt", "magenta", "lime", "cyan", "amber"];
export const ROUTE_WEIGHT_ORDER: readonly RouteWeight[] = ["thin", "regular", "bold", "heavy"];

/** EMBER IS RETIRED (Lee, Sep 22 2026 — SPEC.md section 13 question 3).
 * Symbology v2 makes ember the ROAD family: a graded Forest Service road is
 * #D9573C on paper and #F36F51 over imagery, and RouteLook's Ember
 * (#e0431f) measured ΔE00 4.5 from it — the planned route and the road it
 * runs on became the same red line, which is the one pair on the map that
 * must never collide. No red-orange is free once ember is the road family,
 * so the colour is withdrawn rather than re-hexed; a stored "ember" falls
 * back to Cobalt (isRouteColor below stops recognising it, and
 * loadRouteLook's per-field fallback does the rest). iOS retires the same
 * case in Support/RouteLook.swift. */

/** All five saturated/bright — Lee's ask was "make sure it's BRIGHT so it
 * sticks out," not just "let me pick any color." `hex` is what paints the
 * route line itself; `casing` is the halo underneath it (rule above).
 *
 * THE HEXES ARE NOT THIS FILE'S TO CHOOSE. ios/Dustlark/Support/
 * RouteLook.swift's `RouteColor.hex` switch is the source of truth and
 * these five must equal it — "Amber" has to be one colour, not one per
 * platform, or a rider who sets his phone and then opens the planner on a
 * laptop is looking at a route he didn't pick. Sep 14 2026: four of the
 * six then-shipping colours were different here on day one (Ember #e0472c,
 * Magenta #d6268f, Lime #7ed321 — a plain green where iOS has a
 * yellow-green — and Cyan #00c2d1, a dark teal) and every test still
 * passed, because each side read its own table back to itself.
 * tests/plan-routelook.test.ts now pins all five against the Swift values,
 * as does RouteLookTests on the phone.
 *
 * The two CASING hexes are the exception and are deliberately per
 * platform: each is that platform's own existing cream/soilink (this
 * file's CREAM_CASING is RouteOverlay.ts's pre-existing CASING_COLOR;
 * iOS uses ThemeUI.casing #FFFDF7), so the default repaints unchanged on
 * both. */
export const ROUTE_COLORS: Record<RouteColor, { name: string; hex: string; casing: string }> = {
  cobalt: { name: "Cobalt", hex: "#1e63d6", casing: CREAM_CASING }, // today's default blue
  magenta: { name: "Magenta", hex: "#d61e8c", casing: CREAM_CASING },
  lime: { name: "Lime", hex: "#9ee60a", casing: SOILINK_CASING },
  cyan: { name: "Cyan", hex: "#18e3e3", casing: SOILINK_CASING },
  amber: { name: "Amber", hex: "#ffb300", casing: SOILINK_CASING },
};

export const ROUTE_WEIGHTS: Record<RouteWeight, { name: string; px: number }> = {
  thin: { name: "Thin", px: 3 },
  regular: { name: "Regular", px: 5 },
  bold: { name: "Bold", px: 7 },
  heavy: { name: "Heavy", px: 9 },
};

/** Cobalt/Regular — chosen so the default reproduces the route line's
 * appearance from before this setting existed (see ROUTE_COLORS' and
 * RouteOverlay.ts's own comments): the whole point is that nobody who
 * hasn't touched the new control sees anything change. */
export const DEFAULT_ROUTE_LOOK: RouteLook = { color: "cobalt", weight: "regular" };

const STORAGE_KEY = "routeLook";

// In-memory fallback for a write that misses localStorage (quota, private
// mode, or no localStorage at all) — same degrade-to-memory shape as
// plan/store.ts, scoped to this one key since a RouteLook is a single
// small value rather than that module's keyed document store.
let memory: string | null = null;
let degraded = false;

function getStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function isRouteColor(v: unknown): v is RouteColor {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(ROUTE_COLORS, v);
}

function isRouteWeight(v: unknown): v is RouteWeight {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(ROUTE_WEIGHTS, v);
}

/** Reads the saved RouteLook, falling back PER FIELD rather than as a
 * whole — a valid color paired with an unrecognized/missing weight (an
 * older schema, a hand-edited value, plain corruption) keeps the color and
 * only the weight reverts to default, rather than discarding both. This is
 * also what retires Ember cleanly: a browser that saved `{"color":"ember"}`
 * before Sep 22 2026 now fails isRouteColor and comes back as Cobalt with
 * its chosen WEIGHT intact, rather than losing both halves of the setting.
 * Never
 * throws: a missing/absent localStorage, a non-JSON value, or a value
 * that isn't even an object all land on DEFAULT_ROUTE_LOOK. */
export function loadRouteLook(): RouteLook {
  const ls = getStorage();
  let raw: string | null = null;
  if (ls && !degraded) {
    try {
      raw = ls.getItem(STORAGE_KEY);
    } catch {
      degraded = true;
    }
  }
  if (raw === null) raw = memory;
  if (raw === null) return DEFAULT_ROUTE_LOOK;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_ROUTE_LOOK;
  }
  if (typeof parsed !== "object" || parsed === null) return DEFAULT_ROUTE_LOOK;
  const { color, weight } = parsed as { color?: unknown; weight?: unknown };
  return {
    color: isRouteColor(color) ? color : DEFAULT_ROUTE_LOOK.color,
    weight: isRouteWeight(weight) ? weight : DEFAULT_ROUTE_LOOK.weight,
  };
}

/** Writes the RouteLook, degrading to the in-memory fallback (see above)
 * on quota/private-mode rejection so the session keeps working even
 * though the choice won't survive a reload. */
export function saveRouteLook(look: RouteLook): void {
  const raw = JSON.stringify(look);
  const ls = getStorage();
  if (ls) {
    try {
      ls.setItem(STORAGE_KEY, raw);
      degraded = false;
      memory = null;
      return;
    } catch {
      // Quota exceeded or private-mode write rejection — degrade below.
    }
  }
  degraded = true;
  memory = raw;
}
